import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ThemeConfig {
  name: string;
  isDark: boolean;
  primaryColor: string;
  accentColor: string;
  warnColor: string;
}

export interface BrandConfig {
  brandName: string;
  logoUrl: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    success: string;
    warning: string;
    error: string;
    info: string;
  };
  typography: {
    fontFamily: string;
    fontSize: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
    };
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentThemeSubject = new BehaviorSubject<ThemeConfig>({
    name: 'insee-light',
    isDark: false,
    primaryColor: '#2196f3',
    accentColor: '#ff9800',
    warnColor: '#f44336'
  });

  private brandConfigSubject = new BehaviorSubject<BrandConfig>({
    brandName: 'INSEE Life Insurance',
    logoUrl: '/assets/images/insee-logo.png',
    colors: {
      primary: '#2196f3',
      secondary: '#1976d2',
      accent: '#ff9800',
      success: '#4caf50',
      warning: '#ff9800',
      error: '#f44336',
      info: '#2196f3'
    },
    typography: {
      fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: {
        xs: '0.75rem',
        sm: '0.875rem',
        md: '1rem',
        lg: '1.25rem',
        xl: '1.5rem'
      }
    },
    spacing: {
      xs: '0.5rem',
      sm: '1rem',
      md: '1.5rem',
      lg: '2rem',
      xl: '3rem'
    }
  });

  currentTheme$: Observable<ThemeConfig> = this.currentThemeSubject.asObservable();
  brandConfig$: Observable<BrandConfig> = this.brandConfigSubject.asObservable();

  constructor() {
    // Load theme preference from localStorage
    this.loadThemePreference();
    
    // Listen for system theme changes
    this.setupSystemThemeListener();
  }

  /**
   * Switch between light and dark themes
   */
  toggleTheme(): void {
    const currentTheme = this.currentThemeSubject.value;
    const newTheme: ThemeConfig = {
      ...currentTheme,
      name: currentTheme.isDark ? 'insee-light' : 'insee-dark',
      isDark: !currentTheme.isDark
    };
    
    this.setTheme(newTheme);
  }

  /**
   * Set a specific theme
   */
  setTheme(theme: ThemeConfig): void {
    this.currentThemeSubject.next(theme);
    this.applyThemeToDocument(theme);
    this.saveThemePreference(theme);
  }

  /**
   * Get current theme synchronously
   */
  getCurrentTheme(): ThemeConfig {
    return this.currentThemeSubject.value;
  }

  /**
   * Update brand configuration
   */
  updateBrandConfig(config: Partial<BrandConfig>): void {
    const currentConfig = this.brandConfigSubject.value;
    const newConfig = { ...currentConfig, ...config };
    this.brandConfigSubject.next(newConfig);
    this.saveBrandConfig(newConfig);
  }

  /**
   * Get current brand configuration synchronously
   */
  getBrandConfig(): BrandConfig {
    return this.brandConfigSubject.value;
  }

  private loadThemePreference(): void {
    try {
      const savedTheme = localStorage.getItem('insee-theme');
      if (savedTheme) {
        const theme: ThemeConfig = JSON.parse(savedTheme);
        this.setTheme(theme);
      } else {
        // Default to system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const defaultTheme = this.currentThemeSubject.value;
        defaultTheme.isDark = prefersDark;
        defaultTheme.name = prefersDark ? 'insee-dark' : 'insee-light';
        this.setTheme(defaultTheme);
      }
    } catch (error) {
      console.warn('Failed to load theme preference:', error);
    }
  }

  private saveThemePreference(theme: ThemeConfig): void {
    try {
      localStorage.setItem('insee-theme', JSON.stringify(theme));
    } catch (error) {
      console.warn('Failed to save theme preference:', error);
    }
  }

  private saveBrandConfig(config: BrandConfig): void {
    try {
      localStorage.setItem('insee-brand-config', JSON.stringify(config));
    } catch (error) {
      console.warn('Failed to save brand configuration:', error);
    }
  }

  private setupSystemThemeListener(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', (e) => {
        // Only auto-switch if user hasn't set a manual preference
        const savedTheme = localStorage.getItem('insee-theme');
        if (!savedTheme) {
          const currentTheme = this.currentThemeSubject.value;
          currentTheme.isDark = e.matches;
          currentTheme.name = e.matches ? 'insee-dark' : 'insee-light';
          this.setTheme(currentTheme);
        }
      });
    }
  }

  private applyThemeToDocument(theme: ThemeConfig): void {
    if (typeof document !== 'undefined') {
      const body = document.body;
      
      // Remove existing theme classes
      body.classList.remove('insee-light', 'insee-dark');
      
      // Add new theme class
      body.classList.add(theme.name);
      
      // Update CSS custom properties
      const root = document.documentElement;
      root.style.setProperty('--insee-primary-color', theme.primaryColor);
      root.style.setProperty('--insee-accent-color', theme.accentColor);
      root.style.setProperty('--insee-warn-color', theme.warnColor);
      
      // Update meta theme-color for mobile browsers
      let themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
      if (!themeColorMeta) {
        themeColorMeta = document.createElement('meta');
        themeColorMeta.name = 'theme-color';
        document.head.appendChild(themeColorMeta);
      }
      themeColorMeta.content = theme.primaryColor;
    }
  }
}